// Visual building blocks for the live game-day command center.
import React from 'react';
import Icon from '../../../components/atoms/Icon';
import { InfoTip } from '../components/ConcessionUI';
import { STATUS_COLORS } from '../concessionTheme';
import { formatClock } from './liveMetrics';
import '../concession.css';

// Consecutive overflow points (single points are padded so they still show)
const overflowRuns = (points) => {
  const runs = [];
  let current = null;
  points.forEach((p, i) => {
    if (p.o) {
      if (!current) {
        current = i > 0 ? [points[i - 1], p] : [p];
        runs.push(current);
      } else {
        current.push(p);
      }
    } else if (current) {
      current.push(p);
      current = null;
    }
  });
  return runs.filter((run) => run.length > 1);
};

// Consecutive steps that share a color; each step (p[i-1] -> p[i]) uses the color of its higher end
const colorRuns = (points, valueColor) => {
  const runs = [];
  for (let i = 1; i < points.length; i += 1) {
    const color = valueColor(Math.max(points[i - 1].y, points[i].y));
    const last = runs[runs.length - 1];
    if (last && last.color === color) last.points.push(points[i]);
    else runs.push({ color, points: [points[i - 1], points[i]] });
  }
  return runs;
};

// Formatter for hover values: "14+ min" style (overflow adds "+")
export const unitFormatter = (unit) => (v, overflow) => `${Math.round(v)}${overflow ? '+' : ''}${unit ? ` ${unit}` : ''}`;

// Nearest point to time t (points sorted by t)
const nearestPoint = (points, t) => {
  let lo = 0;
  let hi = points.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (points[mid].t < t) lo = mid;
    else hi = mid;
  }
  return Math.abs(points[lo].t - t) <= Math.abs(points[hi].t - t) ? points[lo] : points[hi];
};

// Tiny line chart of recent values. `target` draws a dashed reference line.
// Hover shows a guide line, a dot and the time + value of the nearest point.
export const Sparkline = ({
  points,
  color = 'var(--q-brand)',
  target,
  height = 44,
  minMax = 0,
  formatValue = unitFormatter(''),
  markers = [], // [{ t, label }] dashed vertical lines, e.g. game start (slides with live data)
  markerLabel = 'below', // 'below' (reserves space under the chart), 'top' (inside) or 'none' (line only; label drawn elsewhere)
  // Optional: (value) => color. Colors each stretch of the line by its own value (e.g. green while
  // within target, red above it) instead of the whole line in one color. The fill stays `color`.
  valueColor,
}) => {
  const gradientId = `spark-${React.useId().replace(/:/g, '')}`;
  const [hover, setHover] = React.useState(null);
  if (!points || points.length < 2) {
    return <div className="cx-spark-empty" style={{ height }}>Not enough data yet</div>;
  }
  const t0 = points[0].t;
  const t1 = points[points.length - 1].t;
  const maxY = Math.max(minMax, target || 0, ...points.map((p) => p.y)) * 1.1 || 1;
  const x = (t) => ((t - t0) / Math.max(1, t1 - t0)) * 100;
  const y = (v) => 100 - (v / maxY) * 100;
  const line = points.map((p) => `${x(p.t).toFixed(2)},${y(p.y).toFixed(2)}`).join(' ');
  const area = `0,100 ${line} 100,100`;

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    setHover(nearestPoint(points, t0 + ratio * (t1 - t0)));
  };

  const hx = hover ? x(hover.t) : 0;

  return (
    <div
      className={`cx-spark-wrap${markers.length && markerLabel === 'below' ? ' has-markers' : ''}`}
      style={{ height, color }}
      onPointerMove={handleMove}
      onPointerLeave={() => setHover(null)}
    >
      <svg className="cx-spark" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.22" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={area} fill={`url(#${gradientId})`} />
        {target !== undefined && (
          <line x1="0" x2="100" y1={y(target)} y2={y(target)} className="cx-spark-target" vectorEffect="non-scaling-stroke" />
        )}
        {valueColor ? (
          // Each step takes the color of its higher end, so a stretch over target always shows
          colorRuns(points, valueColor).map((run, i) => (
            <polyline
              key={i}
              points={run.points.map((p) => `${x(p.t).toFixed(2)},${y(p.y).toFixed(2)}`).join(' ')}
              fill="none"
              stroke={run.color}
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))
        ) : (
          <polyline points={line} fill="none" stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
        )}
        {overflowRuns(points).map((run, i) => (
          <polyline
            key={i}
            points={run.map((p) => `${x(p.t).toFixed(2)},${y(p.y).toFixed(2)}`).join(' ')}
            className="cx-spark-overflow"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
      {markers
        .filter((m) => m.t >= t0 && m.t <= t1)
        .map((m) => (
          <div key={m.label + m.t} className={`cx-spark-marker${markerLabel === 'top' ? ' top' : ''}`} style={{ left: `${x(m.t)}%` }}>
            {markerLabel !== 'none' && (
              <span className={x(m.t) > 85 ? 'edge-right' : x(m.t) < 15 ? 'edge-left' : ''}>{m.label}</span>
            )}
          </div>
        ))}
      {hover && (
        <>
          <div className="cx-spark-guide" style={{ left: `${hx}%` }} />
          <div
            className={`cx-spark-dot${hover.o ? ' overflow' : ''}`}
            style={{ left: `${hx}%`, top: `${y(hover.y)}%`, ...(valueColor && !hover.o ? { background: valueColor(hover.y) } : {}) }}
          />
          <div
            className={`cx-spark-tip${hx > 60 ? ' left' : ''}`}
            style={{ left: `${hx}%` }}
          >
            <span className="cx-spark-tip-time">{formatClock(hover.t)}</span>
            <span className={`cx-spark-tip-value${hover.o ? ' overflow' : ''}`}>
              {formatValue(hover.y, !!hover.o)}
              {hover.o ? ' · Overflow' : ''}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

// "▲ 3 min vs 15m ago". Rising is bad for queue times, counts and transaction times.
export const TrendBadge = ({ delta, unit = 'min', format, compact = false }) => {
  if (delta === null || delta === undefined) {
    return <span className="cx-trend cx-trend-flat">{compact ? '—' : 'No trend yet'}</span>;
  }
  const rounded = Math.round(delta);
  const flat = Math.abs(delta) < 1;
  const cls = flat ? 'cx-trend-flat' : delta > 0 ? 'cx-trend-up' : 'cx-trend-down';
  const icon = flat ? 'dash' : delta > 0 ? 'arrow-up-right' : 'arrow-down-right';
  const amount = format ? format(Math.abs(delta)) : `${Math.abs(rounded)} ${unit}`;
  return (
    <span className={`cx-trend ${cls}`}>
      <Icon name={icon} />
      {flat ? 'Steady' : amount}
      {!compact && <span className="cx-trend-note">vs 15m ago</span>}
    </span>
  );
};

// Status label with an icon (not a dot, which reads as "live")
const STATUS_ICONS = { good: 'check-circle-fill', warn: 'exclamation-circle-fill', bad: 'exclamation-circle-fill' };

export const StatusPill = ({ status, label }) => (
  <span className={`cx-pill cx-pill-${status}`}>
    <Icon name={STATUS_ICONS[status] || 'circle'} className="cx-pill-icon" />
    {label}
  </span>
);

export const OverflowPill = ({ label = 'Overflow' }) => (
  <span className="cx-pill cx-pill-overflow">
    <Icon name="exclamation-triangle-fill" />
    {label}
  </span>
);

// Prominent strip shown when overflow is happening / happened
export const OverflowAlert = ({ title, children }) => (
  <div className="cx-overflow-alert" role="status">
    <Icon name="exclamation-triangle-fill" />
    <div>
      <div className="cx-overflow-alert-title">{title}</div>
      {children && <div className="cx-overflow-alert-body">{children}</div>}
    </div>
  </div>
);

export const KpiCard = ({ label, info, value, accent, children, emphasis = false }) => (
  <div className={`cx-kpi ${emphasis ? 'cx-kpi-emphasis' : ''}`}>
    <div className="cx-kpi-label">
      {label}
      {info && <InfoTip text={info} title={label} />}
    </div>
    <div className="cx-kpi-value" style={accent ? { color: accent } : undefined}>{value}</div>
    {children}
  </div>
);

// One row in the "Today so far" list
export const MetricRow = ({ label, value, at, accent }) => (
  <div className="cx-metric-row">
    <span className="cx-metric-row-label">{label}</span>
    <span className="cx-metric-row-value" style={accent ? { color: accent } : undefined}>
      {value}
      {at && <span className="cx-metric-row-at">@ {at}</span>}
    </span>
  </div>
);

const chunkPairs = (items) => {
  const pairs = [];
  for (let i = 0; i < items.length; i += 2) pairs.push(items.slice(i, i + 2));
  return pairs;
};

// Stat pairs under a tile: labels, values and times share rows so values line up
export const TileDetails = ({ details }) => {
  if (!details || details.length === 0) return null;
  return (
    <div className="cx-tile-details">
      {/* Each pair shares rows (labels, values, times) so values line up even when a label wraps */}
      {chunkPairs(details).map((pair) => (
        <div key={pair.map((d) => d.label).join('|')} className="cx-tile-pair">
          {pair.map((d) => (
            <span key={`l-${d.label}`} className="cx-tile-detail-label">{d.label}</span>
          ))}
          {pair.map((d) => (
            <span
              key={`v-${d.label}`}
              className="cx-tile-detail-value"
              style={d.alert ? { color: 'var(--q-bad)' } : undefined}
            >
              {d.value}
            </span>
          ))}
          {pair.some((d) => d.at) &&
            pair.map((d) => (
              <span key={`a-${d.label}`} className="cx-tile-detail-at">{d.at ? `at ${d.at}` : ''}</span>
            ))}
        </div>
      ))}
    </div>
  );
};

// Per-section live tile for the section board
export const SectionTile = ({
  name,
  value,
  unit,
  status,
  statusLabel,
  trendDelta,
  spark,
  target,
  sparkMin,
  details,
  hideTrend = false,
  valueCaption,
  overflow = false,
  overflowLabel,
  markers,
  // Optional: (value) => status. Colors the sparkline by each reading's own status instead of the current one
  statusFor,
}) => (
  <div className="cx-tile" style={{ '--cx-status-color': STATUS_COLORS[status] }}>
    <div className="cx-tile-head">
      <span className="cx-tile-name">{name}</span>
      {overflow ? (
        <OverflowPill />
      ) : overflowLabel ? (
        <OverflowPill label={overflowLabel} />
      ) : (
        <StatusPill status={status} label={statusLabel} />
      )}
    </div>
    <div className="cx-tile-main">
      <div>
        <div className="cx-tile-value">
          {value}
          {overflow && <span className="cx-plus">+</span>}
          <span className="cx-unit">{unit}</span>
        </div>
        {valueCaption && <div className="cx-tile-caption">{valueCaption}</div>}
      </div>
      {!hideTrend && <TrendBadge delta={trendDelta} unit={unit === 'mins' ? 'min' : unit} />}
    </div>
    <Sparkline
      points={spark}
      color={statusFor ? 'var(--q-text-subtle)' : STATUS_COLORS[status]}
      valueColor={statusFor ? (v) => STATUS_COLORS[statusFor(v)] : undefined}
      target={target}
      minMax={sparkMin}
      height={40}
      formatValue={unitFormatter(unit === 'mins' ? 'min' : unit)}
      markers={markers}
    />
    <TileDetails details={details} />
  </div>
);

// Longest vs shortest line right now
export const LineBalance = ({ longest, shortest, gap, sentence }) => {
  if (!longest || !shortest) return null;
  const max = Math.max(longest.value, 1);
  return (
    <div className="cx-balance">
      <div className="cx-balance-gap">
        <span className="cx-balance-gap-value">{gap}</span>
        <span className="cx-unit">min delta</span>
      </div>
      <p className="cx-balance-text">
        {sentence || (
          <><strong>Section {longest.name}</strong> has a {gap} min longer queue than <strong>Section {shortest.name}</strong>.</>
        )}
      </p>
      {[longest, shortest].map((line) => (
        <div key={line.name} className="cx-balance-row">
          <span className="cx-balance-name">Section {line.name}</span>
          <div className="cx-balance-bar">
            <div
              className="cx-balance-fill"
              style={{ width: `${Math.max(4, (line.value / max) * 100)}%`, background: STATUS_COLORS[line.status] }}
            />
          </div>
          <span className="cx-balance-value">{line.value}{line.overflow ? '+' : ''} min</span>
        </div>
      ))}
    </div>
  );
};

export const UpdatedAt = ({ t, withSeconds = false }) => (
  <span className="cx-updated">
    <Icon name="arrow-repeat" />
    Updated {withSeconds && t
      ? new Date(t).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' })
      : formatClock(t)}
  </span>
);

// Average value per time bucket as horizontal bars, e.g. "when did lines peak"
export const TimeBuckets = ({ buckets, unit, statusFor, markTime, markLabel = 'Game start' }) => {
  if (!buckets || buckets.length === 0) {
    return <div className="cx-spark-empty">No data in this range</div>;
  }
  const max = Math.max(1, ...buckets.map((b) => b.value));
  return (
    <div className="cx-buckets">
      {buckets.map((bucket) => {
        const status = bucket.overflow ? 'bad' : statusFor(bucket.value);
        const isMark = markTime && markTime >= bucket.start && markTime < bucket.end;
        return (
          <div key={bucket.start} className={`cx-bucket ${isMark ? 'cx-bucket-mark' : ''}`}>
            <span className="cx-bucket-time">
              {formatClock(bucket.start)}
              {isMark && <span className="cx-bucket-tag">{markLabel}</span>}
            </span>
            <div className="cx-bucket-bar">
              <div
                className="cx-bucket-fill"
                style={{ width: `${Math.max(3, (bucket.value / max) * 100)}%`, background: STATUS_COLORS[status] }}
              />
            </div>
            <span className="cx-bucket-value">
              {Math.round(bucket.value)}{bucket.overflow ? '+' : ''}
              <span className="cx-bucket-unit">{unit}</span>
              {bucket.overflow ? <Icon name="exclamation-triangle-fill" className="cx-bucket-overflow" /> : null}
            </span>
          </div>
        );
      })}
    </div>
  );
};
